import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";

import { Button } from "@hydra/atom/components";
import { BoxedContent, Header, NoDataFound } from "@/components/common";
import { FinanceMasterTable } from "@/components/finance";
import {
  getLedgerAccountCategoryTableColumns,
  getLedgerAccountCategoryTableData,
} from "@/components/finance/ledger-account-category/ledgerAccountCategoryTableData";
import {
  getLedgerAccountCategories,
  deleteLedgerAccountCategory,
} from "@/api/finance/ledgerAccountCategoryApi";
import { skeletonLoadingRows } from "@/utils/helpers";
import showToast from "@/utils/toast/helpers";

function LedgerAccountCategory() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery(
    ["ledger-account-category"],
    () => getLedgerAccountCategories()
  );

  const queryClient = useQueryClient();
  const deleteMutation = useMutation(deleteLedgerAccountCategory, {
    onError: () => {
      showToast("Could not delete. Try again!", "error");
    },
    onSuccess: () => {
      showToast("Deleted successfully", "success");

      queryClient.invalidateQueries({
        queryKey: ["ledger-account-category"],
      });
    },
  });

  const renderTable = () => {
    if (!isLoading && data && !data?.data) {
      return (
        <NoDataFound
          title="No ledger account category has been added"
          buttonText="Add ledger account category"
          onClick={() => navigate("/finance/ledger-account-category/new")}
        />
      );
    }
    return (
      <FinanceMasterTable
        objectName="LedgerAccountCategory"
        isLoading={isLoading}
        tableColumns={getLedgerAccountCategoryTableColumns(data?.data || [])}
        data={getLedgerAccountCategoryTableData(data?.data || skeletonLoadingRows(5))}
        handleDelete={(id) => deleteMutation.mutate(id)}
      />
    );
  };

  return (
    <BoxedContent>
      <Header
        showBreadcrumb
        leftContent={<h1>Ledger Account Categories</h1>}
        rightContent={(
          <Button
            small
            onClick={() => navigate("/finance/ledger-account-category/new")}
          >
            Add ledger account category
          </Button>
        )}
      />

      {renderTable()}
    </BoxedContent>
  );
}

export default LedgerAccountCategory;
